import * as React from "react";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { eventRepository } from "@/data/repositories";
import type { CreateEventInput, UpdateEventInput } from "@/data/repositories/eventRepository";
import type { Event } from "@/types/domain/event";

interface EventContextValue {
  events: Event[];
  activeEvent: Event | null;
  isLoading: boolean;
  setActiveEventId: (id: string) => void;
  createEvent: (input: CreateEventInput) => Promise<Event>;
  updateEvent: (id: string, patch: UpdateEventInput) => Promise<Event>;
}

const EventContext = React.createContext<EventContextValue | undefined>(undefined);

const ACTIVE_EVENT_KEY = "atelier.active-event-id";

/**
 * The generic Event is now the single active-selection source of truth —
 * WeddingContext (src/contexts/WeddingContext.tsx) is a thin read/write
 * adapter on top of this context, not a second independent store. Nothing
 * else should hold its own "active wedding" state.
 *
 * Scoped to the active Organization, exactly like the old WeddingContext
 * was: resets whenever the organization changes, and persists the active
 * selection to localStorage. All reads/writes go through `eventRepository`
 * (dummy today, swaps to Supabase automatically — see
 * src/data/repositories/index.ts) so this context never touches
 * dummy-data or supabase-js directly.
 */
export function EventProvider({ children }: { children: React.ReactNode }) {
  const { activeOrganization } = useOrganizationContext();
  const [events, setEvents] = React.useState<Event[]>([]);
  const [activeEventId, setActiveEventIdState] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;

    if (!activeOrganization) {
      setEvents([]);
      setActiveEventIdState(null);
      setIsLoading(false);
      return undefined;
    }

    setIsLoading(true);
    eventRepository.listByOrganization(activeOrganization.id).then((orgEvents) => {
      if (cancelled) return;
      setEvents(orgEvents);
      const stored = window.localStorage.getItem(ACTIVE_EVENT_KEY);
      // Never trust a stored id blindly — only accept it if it actually
      // belongs to the organization we just loaded, so switching
      // organizations can never leave an event from another org active.
      const initial = orgEvents.find((e) => e.id === stored) ?? orgEvents[0];
      setActiveEventIdState(initial?.id ?? null);
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [activeOrganization]);

  const setActiveEventId = React.useCallback((id: string) => {
    window.localStorage.setItem(ACTIVE_EVENT_KEY, id);
    setActiveEventIdState(id);
  }, []);

  const createEvent = React.useCallback(async (input: CreateEventInput): Promise<Event> => {
    const created = await eventRepository.create(input);
    setEvents((prev) => [...prev, created]);
    setActiveEventId(created.id);
    return created;
  }, [setActiveEventId]);

  const updateEvent = React.useCallback(async (id: string, patch: UpdateEventInput): Promise<Event> => {
    const updated = await eventRepository.update(id, patch);
    setEvents((prev) => prev.map((e) => (e.id === id ? updated : e)));
    return updated;
  }, []);

  const activeEvent = React.useMemo(
    () => events.find((e) => e.id === activeEventId) ?? null,
    [events, activeEventId]
  );

  const value = React.useMemo<EventContextValue>(
    () => ({ events, activeEvent, isLoading, setActiveEventId, createEvent, updateEvent }),
    [events, activeEvent, isLoading, setActiveEventId, createEvent, updateEvent]
  );

  return <EventContext.Provider value={value}>{children}</EventContext.Provider>;
}

export function useEventContext(): EventContextValue {
  const ctx = React.useContext(EventContext);
  if (!ctx) throw new Error("useEventContext must be used within an EventProvider");
  return ctx;
}
